import React, { useState, useEffect } from 'react';
import { FiMail, FiCheckCircle, FiXCircle, FiBarChart2 } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppHeader from './AppHeader';

const PROVIDER_LABELS = { all: 'All', microsoft: '📧 Microsoft', gmail: '✉️ Gmail' };

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Provider filter — default to the last logged-in provider stored in localStorage
  const [providerFilter, setProviderFilter] = useState(
    () => localStorage.getItem('active_provider') || 'all'
  );
  const userEmail = (localStorage.getItem('ms_user_email') || '').toLowerCase();
  const userRole = localStorage.getItem('ms_user_role') || 'user';
  const isAdmin = userRole === 'admin';

  useEffect(() => {
    loadHistory();
    loadStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerFilter]);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (providerFilter !== 'all') params.append('provider', providerFilter);
    if (userEmail) params.append('user', userEmail);
    params.append('role', userRole);
    return params.toString() ? `?${params.toString()}` : '';
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`/api/history${buildQueryParams()}`);
      setHistory(response.data);
    } catch (error) {
      toast.error('Error loading history');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`/api/history/stats${buildQueryParams()}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <AppHeader />

      {/* Content */}
      <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Email History</h2>
            <p className="text-sm text-neutral-500">View your email sending history and statistics</p>
          </div>
          <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
            {Object.entries(PROVIDER_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => { setLoading(true); setProviderFilter(key); }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                  providerFilter === key
                    ? 'bg-white text-primary-700 shadow-soft'
                    : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
            <div className="bg-white rounded-xl border border-neutral-200 shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-500">Total Emails</p>
                  <p className="text-3xl font-bold text-neutral-900 mt-1">{stats.total || 0}</p>
                </div>
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center">
                  <FiBarChart2 className="w-6 h-6 text-primary-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-neutral-200 shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-500">Sent Successfully</p>
                  <p className="text-3xl font-bold text-success-600 mt-1">{stats.sent || 0}</p>
                  {stats.total > 0 && (
                    <p className="text-xs text-neutral-400 mt-1">{Math.round((stats.sent / stats.total) * 100)}% success rate</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-success-50 rounded-xl flex items-center justify-center">
                  <FiCheckCircle className="w-6 h-6 text-success-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-neutral-200 shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-500">Failed</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">{stats.failed || 0}</p>
                  {stats.total > 0 && (
                    <p className="text-xs text-neutral-400 mt-1">{Math.round((stats.failed / stats.total) * 100)}% failure rate</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                  <FiXCircle className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* History Table */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-soft overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiMail className="w-8 h-8 text-neutral-400" />
              </div>
              <p className="text-neutral-500 font-medium">No email history yet</p>
              <p className="text-neutral-400 text-sm mt-1">
                {providerFilter !== 'all'
                  ? `No emails sent via ${PROVIDER_LABELS[providerFilter]} yet`
                  : 'Sent emails will appear here'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 border-b border-neutral-200">
                  <th className="text-left py-4 px-5 font-semibold text-neutral-600 text-sm">Status</th>
                  <th className="text-left py-4 px-5 font-semibold text-neutral-600 text-sm">Provider</th>
                  <th className="text-left py-4 px-5 font-semibold text-neutral-600 text-sm">From</th>
                  <th className="text-left py-4 px-5 font-semibold text-neutral-600 text-sm">To</th>
                  <th className="text-left py-4 px-5 font-semibold text-neutral-600 text-sm">Subject</th>
                  <th className="text-left py-4 px-5 font-semibold text-neutral-600 text-sm">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-2">
                        {item.status === 'sent' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-success-50 text-success-700">
                            <FiCheckCircle className="w-3.5 h-3.5" /> sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-700">
                            <FiXCircle className="w-3.5 h-3.5" /> failed
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-5">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                        item.provider === 'gmail'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}>
                        {item.provider === 'gmail' ? '✉️ Gmail' : '📧 Microsoft'}
                      </span>
                    </td>
                    <td className="py-4 px-5 text-neutral-700 text-sm">{item.from_email}</td>
                    <td className="py-4 px-5 text-neutral-700 text-sm">{item.to_email}</td>
                    <td className="py-4 px-5 text-neutral-800 text-sm font-medium">{item.subject}</td>
                    <td className="py-4 px-5 text-neutral-500 text-sm">
                      {new Date(item.sentAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;
